
import axios, { AxiosRequestConfig } from 'axios';
import apiConfig from '@/config/apiConfig';
import { 
  Employee, 
  Department,
  Position,
  DashboardStats,
  User,
  Attendance,
  Leave,
  Loan,
  Payslip,
  Council,
} from '@/types';

// Configure axios with settings from config
const apiClient = axios.create({
  baseURL: apiConfig.baseUrl,
  timeout: apiConfig.timeout,
});

// Axios request interceptor for authentication (if needed)
// apiClient.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem('payroll_user');
//     if (token) {
//       config.headers['Authorization'] = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => Promise.reject(error)
// );

/** Extract user-facing message from backend `{ message, status, data }` or axios errors. */
export function getApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
  const err = error as any;
  const body = err?.response?.data ?? err?.data ?? err;
  if (typeof body?.message === 'string' && body.message.trim()) return body.message;
  if (typeof err?.message === 'string' && err.message.trim() && err.message !== 'Error') return err.message;
  return fallback;
}

/** True when body is the app envelope and reports failure (backend often returns HTTP 200). */
export function isApiFailure(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const status = (body as { status?: unknown }).status;
  if (typeof status === 'number') return status !== 200;
  if (typeof status === 'string' && status.trim() !== '') {
    const n = Number(status);
    return !Number.isNaN(n) && n !== 200;
  }
  return false;
}

// Handle API responses and errors consistently — honor application-level `status`
const handleResponse = async (request: Promise<any>) => {
  try {
    const response = await request;
    const data = response.data;

    // Blob/file downloads: return as-is
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      return data;
    }

    if (isApiFailure(data)) {
      const message =
        typeof data?.message === 'string' && data.message.trim()
          ? data.message
          : 'Request failed';
      const error: any = new Error(message);
      error.response = { data, status: data.status };
      error.data = data;
      console.error('API Error:', data);
      throw error;
    }

    return data;
  } catch (error: any) {
    if (error?.response?.data || error?.data) {
      console.error('API Error:', error.response?.data || error.data || error.message);
    } else {
      console.error('API Error:', error?.message || error);
    }
    throw error;
  }
};

// API service for interacting with the PHP backend - now using mock data
export const apiService = {
  // Auth endpoints
  login: (email: string, password: string) => handleResponse(
    apiClient.post('/login', { email, password })
  ),
  resetPassword: (payload: {
    user_id: number;
    current_password: string;
    new_password: string;
  }) => handleResponse(apiClient.post('/reset-password', payload)),

  // Users
  getUsers: (cid?: number | null) => handleResponse(
    cid != null
      ? apiClient.get(`/users/council/${cid}`)
      : apiClient.get('/users')
  ),
  createUser: (user: User) => handleResponse(
    apiClient.post('/users', user)
  ),
  
  // Councils
  getCouncilByCode: (code: number) => handleResponse(
    apiClient.get(`/councils/${code}`)
  ),
  getCouncils: () => handleResponse(
    apiClient.get('/councils')
  ),
  updateUser: (updates: any) => handleResponse(
    apiClient.post(`/usersEdit`, updates)
  ),
  deleteUser: (id: string) => handleResponse(
    apiClient.delete(`/users/${id}`)
  ),

  // Employees
  getEmployees: (cid?: number | null) => handleResponse(
    cid != null
      ? apiClient.get('/employees', { params: { cid } })
      : apiClient.get('/employees')
  ),
  getEmployee: (id: string) => handleResponse(
    apiClient.get(`/employees/${id}`)
  ),
  createEmployee: (employee: Employee) => handleResponse(
    apiClient.post('/employees', employee)
  ),
  updateEmployee: (id: string, employee: Partial<Employee>) => handleResponse(
    apiClient.post('/employees/update', { id, role: 'admin', ...employee })
  ),
  deleteEmployee: (id: string) => handleResponse(
    apiClient.delete(`/employees/${id}`)
  ),

  // Departments
  getDepartments: (cid?: number | null) => handleResponse(
    cid != null
      ? apiClient.get(`/departments/${cid}`)
      : apiClient.get('/departments')
  ),
  createDepartment: (department: { name: string; description?: string; cid?: number | null }) => handleResponse(
    apiClient.post('/departments', department)
  ),
  updateDepartment: (id: string, updates: { name: string; description?: string }) => handleResponse(
    apiClient.post(`/departments/${id}`, updates)
  ),
  deleteDepartment: (id: string) => handleResponse(
    apiClient.delete(`/departments/${id}`)
  ),

  // Positions
  getPositions: (cid?: number | null) => handleResponse(
    cid != null
      ? apiClient.get(`/positions/${cid}`)
      : apiClient.get('/positions')
  ),
  createPosition: (position: { title: string; description?: string; department_id: number }) => handleResponse(
    apiClient.post('/positions', position)
  ),
  updatePosition: (id: string, updates: { title: string; description?: string; department_id: number }) => handleResponse(
    apiClient.post(`/positions/${id}`, updates)
  ),
  deletePosition: (id: string) => handleResponse(
    apiClient.delete(`/positions/${id}`)
  ),

  // Attendance
  getAttendance: (employee_id?: string, date?: string) => handleResponse(
    apiClient.get(`/employees/${employee_id}/attendance`, { params: { date } })
  ),
  createAttendance: (attendanceRecord: Attendance) => handleResponse(
    apiClient.post('/attendance/clock-in', attendanceRecord)
  ),
  updateAttendance: (id: string, updates: Partial<Attendance>) => handleResponse(
    apiClient.post(`/attendance/${id}/update`, updates)
  ),
  deleteAttendance: (id: string) => handleResponse(
    apiClient.delete(`/attendance/${id}`)
  ),

  // Leave
  getEmployeeLeaves: (employee_id?: string) => handleResponse(
    apiClient.get(`/employees/${employee_id}/leaves`)
  ),
  updateLeaveRequest: (id: string, updates: Partial<Leave>) => handleResponse(
    apiClient.post(`/leaves/${id}/status`, updates)
  ),
  deleteLeaveRequest: (id: string) => handleResponse(
    apiClient.delete(`/leaves/${id}`)
  ),

  // Loans
  getLoans: () => handleResponse(
    apiClient.get('/loans')
  ),
  getEmployeeLoans: (employee_id?: string) => handleResponse(
    apiClient.get(`/employees/${employee_id}/loans`)
  ),
  createLoanRequest: (loan: Loan) => handleResponse(
    apiClient.post('/loans/request', loan)
  ),
  updateLoanRequest: (id: string, updates: Partial<Loan>) => handleResponse(
    apiClient.post(`/loans/${id}/status`, updates)
  ),
  deleteLoanRequest: (id: string) => handleResponse(
    apiClient.delete(`/loans/${id}`)
  ),

  // Advances — cid required for council filter; omit call params when invalid
  getAdvances: (cid?: number | null) => {
    const councilId = cid != null && Number(cid) > 0 ? Number(cid) : null;
    return handleResponse(
      councilId
        ? apiClient.get('/advances', { params: { cid: councilId } })
        : apiClient.get('/advances', { params: { cid: 0 } })
    );
  },
  getAdvance: (id: string) => handleResponse(
    apiClient.get(`/advances/${id}`)
  ),
  createAdvanceRequest: (advance: Loan) => handleResponse(
    apiClient.post('/advances/request', advance)
  ),
  approveAdvance: ( data: { id: number; approver_id: number }) => handleResponse(
    apiClient.post(`/advances/approve`, data)
  ),
  rejectAdvance: (data: { id: number; approver_id: number }) => handleResponse(
    apiClient.post(`/advances/reject`, data)
  ),

  // Leave Balance Management
  getEmployeeLeaveBalance: (employeeId: string) => handleResponse(
    apiClient.get(`/leave-balance/employee/${employeeId}`)
  ),
  getEmployeeLeaveHistory: (employeeId: string, year?: number) => handleResponse(
    apiClient.get(`/leave-balance/employee/${employeeId}/history${year ? `?year=${year}` : ''}`)
  ),
  getAllLeaveBalances: (year?: number, month?: number, cid?: number | null) => {
    const params = new URLSearchParams();
    if (year != null) params.set('year', String(year));
    // month kept for backward compat; backend now year-scopes council balances
    if (month != null) params.set('month', String(month));
    if (cid != null && cid > 0) params.set('cid', String(cid));
    const qs = params.toString();
    return handleResponse(
      apiClient.get(`/leave-balance/all${qs ? `?${qs}` : ''}`)
    );
  },
  getLeaveBalanceSummary: (cid?: number | null) => handleResponse(
    cid != null && cid > 0
      ? apiClient.get('/leave-balance/summary', { params: { cid } })
      : apiClient.get('/leave-balance/summary')
  ),
  addLeaveDays: (data: { employee_id: number; year?: number; month?: number; leave_days: number; reason?: string }) => handleResponse(
    apiClient.post('/leave-balance/add', data)
  ),
  setLeaveOpeningBalance: (data: {
    employee_id: number;
    opening_days: number;
    year?: number;
    month?: number;
    reason?: string;
    cid?: number | null;
  }) => handleResponse(
    apiClient.post('/leave-balance/opening-balance', data)
  ),
  useLeaveDays: (data: { employee_id: number; year?: number; month?: number; leave_days_used: number }) => handleResponse(
    apiClient.post('/leave-balance/use', data)
  ),

  // Leave Requests Management
  getLeaveRequests: (status?: string, cid?: number | null) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('cid', String(cid != null && Number(cid) > 0 ? Number(cid) : 0));
    return handleResponse(
      apiClient.get(`/leave-requests?${params.toString()}`)
    );
  },
  getLeaveRequest: (id: string) => handleResponse(
    apiClient.get(`/leave-requests/${id}`)
  ),
  getEmployeeLeaveRequests: (employeeId: string) => handleResponse(
    apiClient.get(`/leave-requests/employee/${employeeId}`)
  ),
  getEmployeeLeaveBalanceForRequest: (employeeId: string) => handleResponse(
    apiClient.get(`/leave-requests/employee/${employeeId}/balance`)
  ),
  createLeaveRequest: (data: { employee_id: number; leave_type: string; start_date: string; end_date: string; days_requested: number; reason?: string }) => handleResponse(
    apiClient.post('/leave-requests/create', data)
  ),
  approveLeaveRequest: (id: string, data?: { reviewer_id?: number; review_notes?: string }) => handleResponse(
    apiClient.post(`/leave-requests/${id}/approve`, data || {})
  ),
  rejectLeaveRequest: (id: string, data?: { reviewer_id?: number; review_notes?: string }) => handleResponse(
    apiClient.post(`/leave-requests/${id}/reject`, data || {})
  ),

  // Payroll
  getAllPayslips: (cid?: number | null) => handleResponse(
    apiClient.get(`/payslips`, {
      params: cid != null && Number(cid) > 0 ? { cid: Number(cid) } : undefined,
    })
  ),
  getPayslipsByPeriod: (period: string, cid?: number | null) => {
    if (cid == null || Number(cid) <= 0) {
      return Promise.resolve({ data: [] });
    }
    return handleResponse(
      apiClient.get(`/payroll/period/${period}`, {
        params: { cid: Number(cid) },
      })
    );
  },
  getPayslipsByEmployee: (employeeId: string) => handleResponse(
    apiClient.get(`/payslips/${employeeId}`)
  ),
  getPayslipById: (id: string) => handleResponse(
    apiClient.get(`/payslips/${id}`)
  ),
  createPayslip: (payslip: Payslip) => handleResponse(
    apiClient.post('/payslips/generate', payslip)
  ),
  updatePayslip: (id: string, updates: Partial<Payslip>) => handleResponse(
    apiClient.post(`/payslips/${id}`, updates)
  ),
  deletePayslip: (id: string) => handleResponse(
    apiClient.delete(`/payslips/${id}`)
  ),

  // Process Payroll (new)
  processPayroll: (payload: any) => handleResponse(
    apiClient.post('/payroll/process', payload)
  ),

  // Payslip Actions
  downloadPayslip: (id: string) => handleResponse(
    apiClient.get(`/payslips/${id}/download`, { responseType: 'blob' })
  ),
  
  printPayslip: (id: string) => handleResponse(
    apiClient.get(`/payslips/${id}/print`, { responseType: 'blob' })
  ),
  
  emailPayslip: (id: string, emailData: { email: string; subject?: string; message?: string }) => handleResponse(
    apiClient.post(`/payslips/${id}/email`, emailData)
  ),
  
  bulkEmailPayslips: (payslipIds: string[], emailData: { email: string; subject?: string; message?: string }) => handleResponse(
    apiClient.post('/payslips/bulk-email', { payslipIds, ...emailData })
  ),
  
  bulkDownloadPayslips: async (payslipIds: string[]): Promise<{ blob: Blob; filename: string }> => {
    try {
      const response = await apiClient.post('/payslips/bulk-download', { payslipIds }, {
        responseType: 'blob',
      });
      const data = response.data as Blob;

      // Backend errors are JSON; with responseType blob they arrive as a JSON blob
      const contentType = String(response.headers?.['content-type'] || data?.type || '');
      if (contentType.includes('application/json') || contentType.includes('text/json')) {
        const text = await data.text();
        let body: unknown = null;
        try {
          body = JSON.parse(text);
        } catch {
          body = null;
        }
        if (isApiFailure(body) || (body && typeof body === 'object' && 'message' in (body as object))) {
          const message =
            typeof (body as { message?: unknown })?.message === 'string' &&
            (body as { message: string }).message.trim()
              ? (body as { message: string }).message
              : 'Bulk download failed';
          const error: any = new Error(message);
          error.response = { data: body, status: (body as { status?: number })?.status ?? 400 };
          error.data = body;
          throw error;
        }
      }

      const disposition = String(response.headers?.['content-disposition'] || '');
      let filename = `payslips-${new Date().toISOString().split('T')[0]}.zip`;
      const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
      if (utfMatch?.[1]) {
        filename = decodeURIComponent(utfMatch[1]);
      } else if (plainMatch?.[1]) {
        filename = plainMatch[1];
      }

      return { blob: data, filename };
    } catch (error: any) {
      // Axios may still return JSON error as a Blob
      const blobData = error?.response?.data;
      if (typeof Blob !== 'undefined' && blobData instanceof Blob) {
        try {
          const text = await blobData.text();
          const body = JSON.parse(text);
          if (body?.message) {
            const err: any = new Error(body.message);
            err.response = { data: body, status: body.status };
            err.data = body;
            throw err;
          }
        } catch (parsed) {
          if (parsed instanceof Error && parsed.message && parsed.message !== error.message) {
            throw parsed;
          }
        }
      }
      throw error;
    }
  },

  // Allowances
  getAllowances: (cid?: number | null) => handleResponse(
    apiClient.get(`/allowances`, {
      params: cid != null ? { cid } : undefined,
    })
  ),
  createAllowance: (allowance: any) => handleResponse(
    apiClient.post('/allowances', allowance)
  ),
  updateAllowance: (id: string, updates: any) => handleResponse(
    apiClient.post(`/allowances/${id}`, { id: Number(id), ...updates })
  ),
  deleteAllowance: (id: string) => handleResponse(
    apiClient.delete(`/allowances/${id}`)
  ),

  // Deductions
  getDeductions: (cid?: number | null) => handleResponse(
    apiClient.get(`/deductions`, {
      params: cid != null ? { cid } : undefined,
    })
  ),
  getUniqueDeductionNames: (cid: number) => handleResponse(
    apiClient.get('/deductions/unique-names', { params: { cid } })
  ),
  getPayslipDeductionsByPeriodAndName: (data: {
    period: string;
    name: string;
    cid: number;
  }) => handleResponse(
    apiClient.post('/deductions/payslip-data', data)
  ),
  createDeduction: (deduction: any) => handleResponse(
    apiClient.post('/deductions', deduction)
  ),
  updateDeduction: (id: string, updates: any) => handleResponse(
    apiClient.post(`/deductions/${id}`, updates)
  ),
  deleteDeduction: (id: string) => handleResponse(
    apiClient.delete(`/deductions/${id}`)
  ),

  // Salary Scales
  getSalaryScales: (cid?: number | null) => handleResponse(
    apiClient.get(`/salary-scales`, {
      params: cid != null ? { cid } : undefined,
    })
  ),
  createSalaryScale: (salaryScale: any) => handleResponse(
    apiClient.post('/salary-scales', salaryScale)
  ),
  updateSalaryScale: (id: string, updates: any) => handleResponse(
    apiClient.post(`/salary-scales/${id}`, updates)
  ),
  deleteSalaryScale: (id: string) => handleResponse(
    apiClient.delete(`/salary-scales/${id}`)
  ),

  // Banks catalog (store bank NAME on employees, not id)
  getBanks: (cid?: number | null) => handleResponse(
    apiClient.get('/banks', {
      params: cid != null ? { cid } : undefined,
    })
  ),
  createBank: (bank: { name: string; code?: string; cid?: number | null; is_active?: boolean }) => handleResponse(
    apiClient.post('/banks', bank)
  ),
  updateBank: (updates: { id: number | string; name?: string; code?: string; is_active?: boolean | number; role: string }) => handleResponse(
    apiClient.post('/banks/update', updates)
  ),
  deleteBank: (payload: { id: number | string; role: string; hard?: boolean }) => handleResponse(
    apiClient.post('/banks/delete', payload)
  ),

  // Employee Salary Management
  getEmployeeSalaries: (employeeId: string) => handleResponse(
    apiClient.get(`/employees/${employeeId}/salaries`)
  ),
  getCurrentEmployeeSalary: (employeeId: string) => handleResponse(
    apiClient.get(`/employees/${employeeId}/current-salary`)
  ),
  createEmployeeSalary: (salary: any) => handleResponse(
    apiClient.post(`/employees/salaries`, salary)
  ),
  updateEmployeeSalary: (id: string, updates: any) => handleResponse(
    apiClient.post(`/employee-salaries/${id}`, updates)
  ),
  deleteEmployeeSalary: (id: string | number, role: string) => handleResponse(
    apiClient.post('/employee-salaries/delete', { id, role: role.toLowerCase() })
  ),

  // Employee Payroll Items
  getEmployeePayrollItems: (employeeId: string) => handleResponse(
    apiClient.get(`/employees/${employeeId}/payroll-items`)
  ),
  getActiveEmployeePayrollItems: (employeeId: string) => handleResponse(
    apiClient.get(`/employees/${employeeId}/active-payroll-items`)
  ),
  createEmployeePayrollItem: (payrollItem: any) => handleResponse(
    apiClient.post(`/employees/payroll-items`, payrollItem)
  ),
  updateEmployeePayrollItem: (id: string, updates: any) => handleResponse(
    apiClient.post(`/employee-payroll-items/${id}`, updates)
  ),
  deleteEmployeePayrollItem: (id: string | number, role: string) => handleResponse(
    apiClient.post('/employee-payroll-items/delete', { id: Number(id), role: role.toLowerCase() })
  ),

  // Audit Logs
  getAuditLogs: (user_id?: string, action?: string, role?: string) => handleResponse(
    apiClient.get('/audit-log', { params: { user_id, action, role } })
  ),
  addAuditLog: (log: any) => handleResponse(
    apiClient.post('/audit-log', log)
  ),
  getAllSalaryStructures: () => handleResponse(
    apiClient.get('/salary-structures')
  ),
 
  getDashboardStats: (cid?: number | null) => handleResponse(
    cid != null
      ? apiClient.get('/dashboard-stats', { params: { cid } })
      : apiClient.get('/dashboard-stats')
  ),
  getPayrollSummary: (cid?: number | null) => handleResponse(
    cid != null
      ? apiClient.get('/payroll-summary', { params: { cid } })
      : apiClient.get('/payroll-summary')
  ),
  // Approval Workflow
  getPayrollApprovalStatus: (period: string, cid?: number | null) => handleResponse(
    apiClient.get(`/payroll/approval-status/${period}`, {
      params: cid != null && cid > 0 ? { cid } : undefined,
    })
  ),
  approvePayroll: (payload: { period: string, role: string, user_id: number, cid?: number | null }) => handleResponse(
    apiClient.post('/payroll/approve', payload)
  ),
  getPayrollPeriodReview: (period: string, cid?: number | null) => handleResponse(
    apiClient.get(`/payroll/period-review/${period}`, {
      params: cid != null ? { cid } : undefined,
    })
  ),
  rerunPayrollPeriod: (payload: {
    period: string;
    role: string;
    user_id: number;
    cid: number;
  }) => handleResponse(
    apiClient.post('/payroll/rerun-period', payload)
  ),
  amendPayslip: (payload: {
    payslip_id: string;
    role: string;
    user_id: number;
    basic_salary?: number;
    notes?: string;
    allowances?: { name: string; amount: number; type?: string }[];
    deductions?: { name: string; amount: number; type?: string }[];
    recalculate_paye?: boolean;
  }) => handleResponse(
    apiClient.post('/payroll/amend-payslip', payload)
  ),

  // Employee Month-Specific Allowances
  getEmployeeAllowances: (employeeId: number | string, month?: string) => handleResponse(
    apiClient.get(`/employees/${employeeId}/allowances${month ? `?month=${month}` : ''}`)
  ),
  getEmployeeAllowancesByMonth: (employeeId: number | string, year: number, month: number) => handleResponse(
    apiClient.get(`/employees/${employeeId}/allowances/${year}/${month}`)
  ),
  createEmployeeAllowance: (employeeId: number | string, data: any) => handleResponse(
    apiClient.post(`/employees/${employeeId}/allowances/add`, data)
  ),
  updateEmployeeAllowance: (employeeId: number | string, id: number | string, data: any) => handleResponse(
    apiClient.post(`/employees/${employeeId}/allowances/${id}/update`, data)
  ),
  deleteEmployeeAllowance: (employeeId: number | string, id: number | string) => handleResponse(
    apiClient.post('/employee-allowances/delete', {
      id: Number(id),
      employee_id: Number(employeeId),
    })
  ),

  // Employee Month-Specific Deductions
  getEmployeeDeductions: (employeeId: number | string, month?: string) => handleResponse(
    apiClient.get(`/employees/${employeeId}/deductions${month ? `?month=${month}` : ''}`)
  ),
  getEmployeeDeductionsByMonth: (employeeId: number | string, year: number, month: number) => handleResponse(
    apiClient.get(`/employees/${employeeId}/deductions/${year}/${month}`)
  ),
  createEmployeeDeduction: (employeeId: number | string, data: any) => handleResponse(
    apiClient.post(`/employees/${employeeId}/deductions/add`, data)
  ),
  updateEmployeeDeduction: (employeeId: number | string, id: number | string, data: any) => handleResponse(
    apiClient.post(`/employees/${employeeId}/deductions/${id}/update`, data)
  ),
  deleteEmployeeDeduction: (employeeId: number | string, id: number | string) => handleResponse(
    apiClient.post('/employee-deductions/delete', {
      id: Number(id),
      employee_id: Number(employeeId),
    })
  ),
};
